import React from 'react'
import '../index.css'
import { useRecoilValue } from 'recoil'
import { AtomColor } from './atom/AtomColor'
import { AtomCurrentProfileUrl } from './atom/AtomCurrentProfileUrl'
import { AtomCurrentBgUrl } from './atom/AtomCurrentBgUrl'
import { AtomWork } from './atom/AtomWork'
import { AtomUsersUrl } from './atom/AtomUsersUrl'
import { AtomUsername } from './atom/AtomUsername'
import { AtomUserid } from './atom/AtomUserid'
import { AtomLevel } from './atom/AtomLevel'
import { AtomBalanceRP } from './atom/AtomBalanceRP'
import { AtomBirthdayDisplay } from './atom/AtomBirthdayDisplay'
import { AtomCurrentBadge1Url } from './atom/AtomCurrentBadge1Url'
import { AtomCurrentBadge2Url } from './atom/AtomCurrentBadge2Url'
import { AtomCurrentBadge3Url } from './atom/AtomCurrentBadge3Url'
import { AtomCurrentBadge4Url } from './atom/AtomCurrentBadge4Url'
import { AtomCurrentFontName } from './atom/AtomCurrentFontName'
import { AtomDescription } from './atom/AtomDescription'

/**
 * Live, fully responsive preview of the Discord profile card. Mirrors the
 * layout of the Puppeteer render target (/getprofile/:id) but with fluid
 * CSS so it looks right at ANY width — no fixed 1000x1480 sizing, no
 * scale transforms. The render target itself is untouched.
 */
const Profile = () => {
  const mycolor = useRecoilValue(AtomColor)
  const currentBgUrl = useRecoilValue(AtomCurrentBgUrl)
  const work = useRecoilValue(AtomWork)
  const profileImageUrl = useRecoilValue(AtomCurrentProfileUrl)
  const avatarUrl = useRecoilValue(AtomUsersUrl)
  const username = useRecoilValue(AtomUsername)
  const userid = useRecoilValue(AtomUserid)
  const level = useRecoilValue(AtomLevel)
  const balanceRp = useRecoilValue(AtomBalanceRP)
  const birthday = useRecoilValue(AtomBirthdayDisplay)
  const badge1 = useRecoilValue(AtomCurrentBadge1Url)
  const badge2 = useRecoilValue(AtomCurrentBadge2Url)
  const badge3 = useRecoilValue(AtomCurrentBadge3Url)
  const badge4 = useRecoilValue(AtomCurrentBadge4Url)
  const curfont = useRecoilValue(AtomCurrentFontName)
  const description = useRecoilValue(AtomDescription)

  const badges = [badge1, badge2, badge3, badge4].filter(Boolean)

  return (
    <div
      className="w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-cover bg-center"
      style={{ backgroundImage: `url('${currentBgUrl}')`, color: mycolor }}
    >
      {/* Glass overlay — same treatment as the rendered card */}
      <div
        className="w-full backdrop-blur-xl"
        style={{ backgroundColor: 'rgba(82,84,85,.25)' }}
      >
        {/* Work title bar */}
        <h1 className="font-Roboto font-bold text-lg sm:text-2xl px-4 py-3">
          {work}
        </h1>

        {/* Profile image — same 1000:542 proportion as the rendered card */}
        {profileImageUrl && (
          <img
            src={profileImageUrl}
            key={profileImageUrl}
            alt="profile"
            className="w-full aspect-[1000/542] object-cover object-center"
          />
        )}

        {/* Avatar overlapping the image + name/id */}
        <div className="flex items-end gap-4 px-4 -mt-10 sm:-mt-14">
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt="avatar"
              className="h-20 w-20 sm:h-28 sm:w-28 rounded-full border-4 border-white/20 object-cover flex-shrink-0"
            />
          )}
          <div className="pb-1 min-w-0">
            <h1 className="font-Roboto font-bold text-lg sm:text-2xl truncate">{username}</h1>
            <p className="font-poppins text-sm sm:text-base opacity-90 truncate">{userid}</p>
          </div>
        </div>

        {/* Stats + badges */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 px-4 pt-6">
          <div className="flex gap-6 sm:gap-8">
            <div>
              <h1 className="font-Roboto font-bold text-base sm:text-xl">Level</h1>
              <p className="font-poppins text-sm sm:text-lg">{level}</p>
            </div>
            <div>
              <h1 className="font-Roboto font-bold text-base sm:text-xl">BalanceRp</h1>
              <p className="font-poppins text-sm sm:text-lg">{balanceRp}</p>
            </div>
            <div>
              <h1 className="font-Roboto font-bold text-base sm:text-xl">Birthday</h1>
              <p className="font-poppins text-sm sm:text-lg">{birthday}</p>
            </div>
          </div>
          {badges.length > 0 && (
            <div className="sm:text-center">
              <h1 className="font-Roboto font-bold text-base sm:text-xl mb-1">Badges</h1>
              <div className="grid grid-cols-4 sm:grid-cols-2 gap-2 w-fit">
                {badges.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`badge${i + 1}`}
                    className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="px-4 pt-6 pb-8 break-words">
          <h1 className="font-Roboto font-bold text-base sm:text-xl mb-1">Description</h1>
          <p className="text-sm sm:text-base leading-relaxed" style={{ fontFamily: curfont }}>
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default Profile
